import { useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import StripeInput from './StripeInput';

const StripeKey = import.meta.env.VITE_STRIPE_KEY;
const stripePromise = loadStripe(StripeKey);

interface StripeProps {
  onClose: () => void;
}
const StripeForm = ({ onClose }: StripeProps) => {
  const api = useApi();
  const stripe = useStripe();
  const elements = useElements();
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [address1, setAddress1] = useState<string>('');
  const [address2, setAddress2] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [country, setCountry] = useState<string>('US');
  const [agree, setAgree] = useState<boolean>(false);

  const stripePost = useMutation(
    api.setupBillingStripeSubscriptionApiV1SetupBillingStripeSubscriptionPost,
    {
      onSuccess() {
        toast.success('Billing successfully added!');
        onClose();
      },
      onError: apiError,
    },
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const card = elements.getElement(CardNumberElement);

    if (card == null) {
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name: `${firstName} ${lastName}`,
        email,
        phone,
        address: {
          line1: address1,
          line2: address2,
          city,
          state,
          postal_code: zip,
          country,
        },
      },
    });

    if (error) {
      toast.error(error?.message, {
        autoClose: false,
        hideProgressBar: true,
      });
    } else {
      // console.log('[PaymentMethod]', paymentMethod);
      stripePost.mutate({
        name: paymentMethod.billing_details.name || `${firstName} ${lastName}`,
        email: paymentMethod.billing_details.email || email,
        phone: paymentMethod.billing_details.phone || phone,
        street_addr_1: paymentMethod.billing_details.address?.line1 || address1,
        street_addr_2: paymentMethod.billing_details.address?.line2 || address2,
        city: paymentMethod.billing_details.address?.city || city,
        state: paymentMethod.billing_details.address?.state || state,
        postal_code: paymentMethod.billing_details.address?.postal_code || zip,
        country: paymentMethod.billing_details.address?.country || country,
        payment_method_id: paymentMethod.id,
      });
    }
  };

  const validateForm = () => {
    return (
      !stripe ||
      !firstName ||
      !lastName ||
      !email ||
      !phone ||
      !address1 ||
      !city ||
      !state ||
      !zip ||
      !agree
    );
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
      <Typography variant="body2">
        To get started, enter your details in the fields below.
      </Typography>
      <Box sx={{ my: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              User Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Address Details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Billing Address 1"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Billing Address 2"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State"
              value={state}
              onChange={(e) => setState(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Postal Code"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid>
          {/* <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              margin="dense"
              size="small"
              required
            />
          </Grid> */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Card Details
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Credit Card Number"
              name="ccnumber"
              required
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                inputComponent: StripeInput,
                inputProps: {
                  component: CardNumberElement,
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Expiration Date"
              name="ccexp"
              required
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                inputProps: {
                  component: CardExpiryElement,
                },
                inputComponent: StripeInput,
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="CVC"
              name="cvc"
              required
              fullWidth
              margin="dense"
              size="small"
              InputProps={{
                inputProps: {
                  component: CardCvcElement,
                },
                inputComponent: StripeInput,
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        By confirming your subscription, you allow us to charge your card for this and future
        payments in accordance with terms. You can always cancel your subscription.
      </Typography>
      <Box sx={{ mb: 5 }}>
        <FormControlLabel
          value="end"
          control={
            <Checkbox
              size="small"
              checked={agree}
              inputProps={{ 'aria-label': 'controlled' }}
              onChange={(e) => setAgree(e.target.checked)}
            />
          }
          label="I agree to the terms and conditions"
          labelPlacement="end"
          sx={{ width: '100%', gap: 2 }}
        />
      </Box>
      <Button type="submit" variant="contained" fullWidth disabled={validateForm()}>
        Submit Billing
      </Button>
    </Box>
  );
};
StripeForm.displayName = 'StripeForm';

interface StripeProps {
  onClose: () => void;
}
const Stripe = ({ onClose }: StripeProps) => {
  return (
    <Box>
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <StripeForm onClose={onClose} />
        </Elements>
      ) : (
        <Alert severity="error" sx={{ mb: 4 }}>
          Stripe key not found
        </Alert>
      )}
    </Box>
  );
};
Stripe.displayName = 'Stripe';
export default Stripe;
